/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {TooltipEnum} from './tooltip.enum';
import {NgbTooltip, NgbTooltipConfig} from '@ng-bootstrap/ng-bootstrap';
import {TooltipAlignmentEnum} from './tooltip-alignment.enum';
import {TooltipIconSizeEnum} from './tooltip-icon-size.enum';
import {Subscription} from 'rxjs';
import { v4 as uuid } from 'uuid';

@Component({
    selector: 'app-tooltip',
    templateUrl: './tooltip.component.html',
    styleUrls: ['./tooltip.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TooltipComponent implements OnInit, OnDestroy {
    @Input() tooltipType: TooltipEnum = TooltipEnum.NONE;
    @Input() message: string | string[] | TemplateRef<any>;
    @Input() disable = false;
    @Input() alignIcon: TooltipAlignmentEnum = TooltipAlignmentEnum.RIGHT;
    @Input() iconSize: TooltipIconSizeEnum = TooltipIconSizeEnum.SMALL;
    @Input() alignContent: TooltipAlignmentEnum;
    @Input() dynamic = true;
    @Input() orderedList = true;
    @Input() tooltipCustomClass: string;
    @Input() placement = 'auto';
    @Input() manualClose = new EventEmitter<void>();
    @Input() manualOpen = new EventEmitter<void>();
    @Output() shown = new EventEmitter<void>();
    @Output() hidden = new EventEmitter<void>();
    @ViewChild('tooltip', {static: true}) tooltip: TemplateRef<any>;
    @ViewChild('tooltipComponent') tooltipComponent: NgbTooltip;
    @HostBinding('attr.data-tooltip-id') readonly UUID = uuid().replace(/-/g, '');
    public readonly CSS_BASE_CLASS = `orcan-tooltip tooltip-id-${this.UUID}`;
    TooltipEnum = TooltipEnum;
    TooltipAlignmentEnum = TooltipAlignmentEnum;
    TooltipIconSizeEnum = TooltipIconSizeEnum;
    public showDynamicTooltip = true;
    private subscriptions = new Subscription();

    constructor(private config: NgbTooltipConfig) {
        // Changing through the service will affect *ALL* tooltips, not just the ones created by this component instance.
        config.openDelay = 1000;
        config.closeDelay = 100;
        config.container = 'body';
    }

    ngOnInit(): void {
        this.close();
        this.open();
    }

    /**
     * Checks if {@link message} is a string
     * @returns boolean
     * **/
    isMessageString(): boolean {
        return typeof this.message === 'string';
    }

    /**
     * Determines the custom class to be passed to the tooltip component
     * @returns string The CSS class
     * **/
    getCssClass(): string {
        let finalCss = this.CSS_BASE_CLASS;

        switch (this.alignContent) {
            case TooltipAlignmentEnum.LEFT:
                finalCss = `${finalCss} content-align-left`;
                break;
            case TooltipAlignmentEnum.RIGHT:
                finalCss = `${finalCss} content-align-right`;
                break;
        }

        if (this.tooltipCustomClass != null) {
            finalCss = `${finalCss} ${this.tooltipCustomClass}`;
        }

        return finalCss;
    }

    /**
     * Determines the tooltip content
     * @returns the tooltip content TemplateRef
     */
    getTooltipContent(): string | string[] | TemplateRef<any> {
        // When optimized, abstract classes have their constructor name changed to something generic, so we must
        // check what the types that the variable isn't. In this case, the variable must not be array or string.
        if (this.message != null && this.message.constructor.name !== Array.name && this.message.constructor.name !== String.name) {
            return this.message;
        }
        return this.tooltip;
    }

    /**
     * Determines if the tooltip should dynamic appear or not
     * @param event The event that triggers the calculation
     */
    disableTooltip(event: any): void {
        if (!this.disable) {
            const element = event.target as Element;
            this.showDynamicTooltip = !(element.scrollWidth > element.clientWidth);
        } else {
            this.showDynamicTooltip = this.disable;
        }
    }

    /**
     * Emits the shown event
     */
    emitShown(): void {
        this.shown.emit();
    }

    /**
     * Emits the hidden event
     */
    emitHidden(): void {
        this.hidden.emit();
    }

    /**
     * Manually closes the tooltip
     */
    close(): void {
        this.subscriptions.add(
                this.manualClose.subscribe(() => {
                    this.tooltipComponent.close();
                }));
    }

    /**
     * Manually opens the tooltip
     */
    open(): void {
        this.subscriptions.add(
                this.manualOpen.subscribe(() => {
                    this.tooltipComponent.open();
                }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

}
